// 'use client' is load-bearing: `useBodyScrollLock` below calls a React hook and
// this module is reachable from the `@auctogon/ui/web` barrel. Guarded by
// web-barrel-client-directive.test.ts (BL-1124).
'use client';

/**
 * Reference-counted document body scroll lock for modal overlays — the one
 * implementation for both apps (lifted from bidder-web in BUG-1849, when admin's
 * four overlays were still each setting `overflow: hidden` on their own).
 *
 * Plain `overflow: hidden` is enough on desktop, but iOS Safari still rubber-bands
 * the page behind a modal. The first acquire applies `position: fixed` with
 * `top: -scrollY` so the visual viewport stays put; the last release restores
 * inline styles and `window.scrollTo` so the user lands back where they were.
 *
 * Callers receive a one-shot `release` function from `acquireBodyScrollLock()` and
 * must invoke it on cleanup (typically a React effect return), or use
 * `useBodyScrollLock(active)`, which does exactly that.
 */

import { useEffect } from 'react';

let lockCount = 0;
let savedScrollY = 0;
let savedOverflow = '';
let savedPosition = '';
let savedTop = '';
let savedWidth = '';
let savedPaddingRight = '';

/**
 * Hold the body scroll lock for as long as `active` is true.
 *
 * Keyed only on `active`, so a parent re-render does not churn the ref-counted
 * lock while the overlay stays open.
 *
 * @param active - Whether the calling overlay is currently open.
 * @returns Nothing.
 * @sideEffect Pins `document.body` while active; restores it and the scroll position on release.
 */
export function useBodyScrollLock(active: boolean): void {
    useEffect(() => {
        if (!active) return;
        return acquireBodyScrollLock();
    }, [active]);
}

/**
 * Apply iOS-safe scroll freeze when the first modal opens.
 *
 * @returns A release function; call it once when this holder closes.
 * @sideEffect Pins `document.body` (overflow, position, top, width) on the first acquire.
 */
export function acquireBodyScrollLock(): () => void {
    if (typeof document === 'undefined' || typeof window === 'undefined') {
        return () => {};
    }

    lockCount += 1;
    if (lockCount === 1) {
        savedScrollY = window.scrollY;
        const { body } = document;
        savedOverflow = body.style.overflow;
        savedPosition = body.style.position;
        savedTop = body.style.top;
        savedWidth = body.style.width;
        savedPaddingRight = body.style.paddingRight;

        const scrollbarWidth = Math.max(
            0,
            window.innerWidth - document.documentElement.clientWidth,
        );
        const existingPadding = parseFloat(getComputedStyle(body).paddingRight || '0');
        body.style.overflow = 'hidden';
        body.style.position = 'fixed';
        body.style.top = `-${savedScrollY}px`;
        body.style.width = '100%';
        // Pad for the scrollbar only when hiding overflow actually removes it.
        // globals.css reserves the gutter permanently (`html { scrollbar-gutter:
        // stable }`, BUG-809), and a reserved gutter survives `overflow: hidden`
        // — so the measured width is still on screen and padding for it again
        // narrows the body by that width, shifting the sticky header left
        // while a modal is open (BUG-1563).
        if (scrollbarWidth > 0 && !rootReservesScrollbarGutter()) {
            body.style.paddingRight = `${existingPadding + scrollbarWidth}px`;
        }
    }

    return () => releaseBodyScrollLock();
}

/**
 * Whether the root scroll container keeps its scrollbar gutter under
 * `overflow: hidden`. Browsers without `scrollbar-gutter` support report an
 * empty value and fall through to the compensating path, which is correct
 * there: with no reservation the bar really does disappear when locked.
 */
function rootReservesScrollbarGutter(): boolean {
    return getComputedStyle(document.documentElement)
        .getPropertyValue('scrollbar-gutter')
        .includes('stable');
}

/** Drop one lock holder; restore the page when the last modal closes. */
export function releaseBodyScrollLock(): void {
    if (typeof document === 'undefined' || typeof window === 'undefined') return;
    if (lockCount === 0) return;

    lockCount -= 1;
    if (lockCount > 0) return;

    const { body } = document;
    body.style.overflow = savedOverflow;
    body.style.position = savedPosition;
    body.style.top = savedTop;
    body.style.width = savedWidth;
    body.style.paddingRight = savedPaddingRight;
    window.scrollTo({ top: savedScrollY, behavior: 'instant' });
}

/** Reset module state between test cases — import via `@auctogon/ui/hooks/bodyScrollLock.test-helpers` only. */
export function resetBodyScrollLockForTests(): void {
    lockCount = 0;
    savedScrollY = 0;
    savedOverflow = '';
    savedPosition = '';
    savedTop = '';
    savedWidth = '';
    savedPaddingRight = '';
}
